import { OverlayContainer } from '@angular/cdk/overlay';
import {
  Component,
  OnInit,
  OnDestroy,
  Renderer2,
  HostListener,
} from '@angular/core';

import { TokenDetails } from './models/auth/token-details.type';
import { BreadCrumbService } from './services/bread-crumb/bread-crumb.service';
import { CubeService } from './services/cube/cube.service';
import { MigrationService } from './services/migration/migration.service';
import { IdbService } from './services/storage/idb.service';
import { STORAGE_KEYS } from './services/storage/storage.service';
import { SyncService } from './services/sync/sync.service';
import { ThemeService } from './services/theme/theme.service';
import { AuthActions } from './store/actions/auth.actions';
import { AppState } from './store/state/app.state';
import {
  selectTokenDetails,
  selectUserTenants,
} from './store/selectors/auth.selectors';

import { concatLatestFrom } from '@ngrx/operators';

import { Store } from '@ngrx/store';
import { PrimeNG } from 'primeng/config';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'cube-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  destroy$: Subject<void> = new Subject<void>();
  isDarkTheme: boolean;

  private subscriptions: Subscription[] = [];

  constructor(
    private breadCrumbService: BreadCrumbService,
    private cubeService: CubeService,
    private idbService: IdbService,
    private migrationService: MigrationService,
    private overlayContainer: OverlayContainer,
    private primeNg: PrimeNG,
    private readonly store: Store<AppState>,
    private renderer: Renderer2,
    private syncService: SyncService,
    private themeService: ThemeService
  ) {}

  getRouteAnimation(outlet) {
    return outlet.activatedRouteData.animation;
  }

  ngOnInit() {
    this.breadCrumbService.setEventListeners();

    this.subscriptions.push(
      this.themeService.isDarkTheme.subscribe((isDark) => {
        this.isDarkTheme = isDark;
        this.overlayContainer
          .getContainerElement()
          .classList.remove(isDark ? 'light-theme' : 'dark-theme');
        this.overlayContainer
          .getContainerElement()
          .classList.add(!isDark ? 'light-theme' : 'dark-theme');

        this.primeNg.overlayOptions = {
          styleClass: isDark ? 'dark-theme' : 'light-theme',
        };
      })
    );

    fromEvent(window, 'storage')
      .pipe(filter((ev: StorageEvent) => ev.key === STORAGE_KEYS.currentTenant))
      .subscribe((storageEvent: StorageEvent) => {
        this.cubeService.cleanCache();
        window.location.reload();
      });

    this.primeNg.ripple.set(true);

    this.renderer.setStyle(document.body, 'overscroll-behavior-y', 'contain');
    this.renderer.setStyle(document.body, 'overscroll-behavior-x', 'contain');

    // Setup indexedDB
    this.idbService.initializeDatabase();
    this.migrationService.migrateIdbMasterTables(); // TEMP: For migration

    this.store
      .select(selectTokenDetails)
      .pipe(
        filter(
          (
            tokenDetails: TokenDetails | undefined
          ): tokenDetails is TokenDetails => !!tokenDetails
        ),
        concatLatestFrom(() => this.store.select(selectUserTenants)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ([tokenDetails, tenants]) => {
          // TEMP: For B2C migration
          //INFO - Update token after retrieving it for use in the web worker.
          this.cubeService.updateToken(tokenDetails.idToken);
          if (!tenants.length) {
            this.store.dispatch(AuthActions.getUserTenants({ tokenDetails }));
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  @HostListener('window:beforeunload')
  onWindowUnload(): void {
    this.syncService.onPageReload();
  }
}
